/**
 * Non-Sprout Total Pricing
 * Simple total price display for non-sprout products
 * Shows: variant price × quantity, with savings vs single item pricing
 */
class NonSproutTotalPricing {
  constructor() {
    this.totalPriceContainer = null;
    this.currentVariant = null;
    this.variantChangeUnsubscriber = null;
    this.totalPriceElements = null;
    this.init();
  }
  init() {
    try {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.setupNonSproutPricing());
      } else {
        this.setupNonSproutPricing();
      }
    } catch (error) {
      // Silent error handling
    }
  }

  getProductContext() {
    return document.querySelector('product-info[data-product-id]') || document;
  }

  isNonSproutProduct() {
    const currentUrl = window.location.pathname.toLowerCase();
    const productTitle = document.querySelector('.product__title, h1')?.textContent?.toLowerCase() || '';
    const bodyClasses = document.body.className.toLowerCase();
    const productHandle = document.querySelector('[data-product-handle]')?.getAttribute('data-product-handle')?.toLowerCase() || '';
    const metaTitle = document.querySelector('meta[property="og:title"]')?.content?.toLowerCase() || '';
    const pageTitle = document.title.toLowerCase();
    
    // Return true if NOT a sprout product
    return !(
      currentUrl.includes('sprout') || currentUrl.includes('little-oak') || productTitle.includes('little oak') || productTitle.includes('littleoak') || productHandle.includes('little-oak') || !!document.querySelector('#little-oak-quantity-table') ||
      productTitle.includes('sprout') ||
      pageTitle.includes('sprout') ||
      metaTitle.includes('sprout') ||
      productHandle.includes('sprout') ||
      bodyClasses.includes('sprout-product-template') ||
      document.querySelector('[data-product-handle*="sprout"]') ||
      document.querySelector('[data-monster-deals]') ||
      window.location.href.includes('sprout') ||
      document.querySelector('template[id*="sprout"]') !== null ||
      // Sprout tier table is rendered for vendor-matched brands too (e.g. Healthy Heights)
      !!document.querySelector('#sprout-quantity-table')
    );
  }

  setupNonSproutPricing() {
    // Only run on non-sprout products
    if (!this.isNonSproutProduct()) {
      return;
    }
    
    // Create total price display
    this.createTotalPriceDisplay();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Initial update
    this.updatePricing();
  }

  createTotalPriceDisplay() {
    const productContext = this.getProductContext();
    const host = productContext.querySelector('[data-total-price-host]');
    let totalPriceContainer =
      (host && (host.querySelector('.non-sprout-total-price') || host.querySelector('.total-price'))) ||
      null;

    if (host && !totalPriceContainer) {
      totalPriceContainer = host;
    }

    if (!totalPriceContainer) {
      const quantityWrapper =
        productContext.querySelector('.product-form__quantity:not(.quick-add-hidden)') ||
        productContext.querySelector('.product-form__quantity');

      if (!quantityWrapper) return;

      const priceContainer =
        quantityWrapper.querySelector('.price-per-item__container') ||
        quantityWrapper.querySelector('.quantity-price-wrapper') ||
        quantityWrapper;

      if (!priceContainer) return;

      totalPriceContainer =
        priceContainer.querySelector('.non-sprout-total-price') ||
        priceContainer.querySelector('.total-price');
    }

    if (!totalPriceContainer) {
      totalPriceContainer = document.createElement('span');
      if (host) {
        host.appendChild(totalPriceContainer);
      } else {
        return;
      }
    }

    totalPriceContainer.classList.add('non-sprout-total-price', 'total-price', 'current-total');
    totalPriceContainer.setAttribute('aria-live', 'polite');

    if (totalPriceContainer.dataset.nspInitialized === 'true') {
      this.totalPriceContainer = totalPriceContainer;
      this.totalPriceElements = {
        current: totalPriceContainer.querySelector('.non-sprout-total-price__current-value') ||
          totalPriceContainer.querySelector('.current-total'),
        currentLabel: totalPriceContainer.querySelector('.non-sprout-total-price__current-label'),
        originalWrapper: totalPriceContainer.querySelector('.non-sprout-total-price__original') ||
          totalPriceContainer.querySelector('.original-total'),
        originalLabel: totalPriceContainer.querySelector('.non-sprout-total-price__original-label'),
        originalValue: totalPriceContainer.querySelector('.non-sprout-total-price__original-value') ||
          totalPriceContainer.querySelector('.original-total'),
        savingsWrapper: totalPriceContainer.querySelector('.non-sprout-total-price__savings') ||
          totalPriceContainer.querySelector('.savings-display'),
        savingsLabel: totalPriceContainer.querySelector('.non-sprout-total-price__savings-label'),
        savingsValue: totalPriceContainer.querySelector('.non-sprout-total-price__savings-value') ||
          (totalPriceContainer.querySelector('.savings-display') &&
            totalPriceContainer.querySelector('.savings-display span:last-child'))
      };
      return;
    }

    totalPriceContainer.dataset.nspInitialized = 'true';
    totalPriceContainer.textContent = '';

    totalPriceContainer.style.display = 'inline-flex';
    totalPriceContainer.style.flexDirection = 'column';
    totalPriceContainer.style.alignItems = 'flex-end';
    totalPriceContainer.style.gap = '0.25rem';
    totalPriceContainer.style.fontSize = '1.4rem';
    totalPriceContainer.style.fontWeight = '600';
    totalPriceContainer.style.color = '#333';

    const totalLine = document.createElement('span');
    totalLine.className = 'non-sprout-total-price__current-line';
    totalLine.style.display = 'flex';
    totalLine.style.alignItems = 'baseline';
    totalLine.style.gap = '0.4rem';

    const totalLabel = document.createElement('span');
    totalLabel.className = 'non-sprout-total-price__current-label';
    totalLabel.textContent = 'Total:';

    const totalValue = document.createElement('span');
    totalValue.className = 'current-total non-sprout-total-price__current-value';
    totalValue.style.whiteSpace = 'nowrap';

    totalLine.append(totalLabel, totalValue);
    totalPriceContainer.appendChild(totalLine);

    const originalWrapper = document.createElement('span');
    originalWrapper.className = 'original-total non-sprout-total-price__original';
    originalWrapper.style.display = 'none';
    originalWrapper.style.textDecoration = 'line-through';
    originalWrapper.style.color = '#999';
    originalWrapper.style.fontWeight = '500';
    originalWrapper.style.whiteSpace = 'nowrap';
    originalWrapper.style.alignItems = 'baseline';
    originalWrapper.style.gap = '0.4rem';

    const originalLabel = document.createElement('span');
    originalLabel.className = 'non-sprout-total-price__original-label';
    originalLabel.textContent = 'Original:';
    originalLabel.style.marginRight = '0.4rem';

    const originalValue = document.createElement('span');
    originalValue.className = 'non-sprout-total-price__original-value';

    originalWrapper.append(originalLabel, originalValue);
    totalPriceContainer.appendChild(originalWrapper);

    const savingsWrapper = document.createElement('span');
    savingsWrapper.className = 'savings-display non-sprout-total-price__savings';
    savingsWrapper.style.display = 'none';
    savingsWrapper.style.fontSize = '1.2rem';
    savingsWrapper.style.fontWeight = '600';
    savingsWrapper.style.color = '#428426';
    savingsWrapper.style.whiteSpace = 'nowrap';
    savingsWrapper.style.alignItems = 'baseline';
    savingsWrapper.style.gap = '0.4rem';

    const savingsLabel = document.createElement('span');
    savingsLabel.className = 'non-sprout-total-price__savings-label';
    savingsLabel.textContent = 'You Saved:';
    savingsLabel.style.marginRight = '0.4rem';

    const savingsValue = document.createElement('span');
    savingsValue.className = 'non-sprout-total-price__savings-value';

    savingsWrapper.append(savingsLabel, savingsValue);
    totalPriceContainer.appendChild(savingsWrapper);

    this.totalPriceContainer = totalPriceContainer;
    this.totalPriceElements = {
      current: totalValue,
      currentLabel: totalLabel,
      originalWrapper,
      originalLabel,
      originalValue,
      savingsWrapper,
      savingsLabel,
      savingsValue
    };
  }

  setupEventListeners() {
    const debouncedUpdate = this.debounce(() => {
      this.updatePricing();
    }, 300);

    // Listen for quantity changes
    document.addEventListener('input', (e) => {
      if (e.target.matches('input[name="quantity"], .quantity__input, input[type="number"][id*="Quantity"]')) {
        debouncedUpdate();
      }
    });

    document.addEventListener('change', (e) => {
      if (e.target.matches('input[name="quantity"], .quantity__input, input[type="number"][id*="Quantity"]')) {
        this.updatePricing();
      }
    });

    // Listen for variant changes
    document.addEventListener('variant:change', () => {
      setTimeout(() => {
        this.updatePricing();
      }, 100);
    });

    // Listen for Shopify's pub-sub variant change events
    if (window.subscribe && window.PUB_SUB_EVENTS) {
      this.variantChangeUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.variantChange, (event) => {
        this.currentVariant = event.data.variant;
        
        setTimeout(() => {
          this.updatePricing();
        }, 100);
      });
    }

    // Listen for variant price button clicks
    document.addEventListener('click', (e) => {
      if (e.target.matches('.variant-price-button, [data-variant-price]') || 
          e.target.closest('.variant-price-button, [data-variant-price]')) {
        setTimeout(() => {
          this.updatePricing();
        }, 50);
      }
    });

    // Listen for variant selection changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('select[name="id"], input[name="id"]')) {
        setTimeout(() => {
          this.updatePricing();
        }, 50);
      }
    });
    
    // Listen for quantity button clicks (+ and - buttons)
    document.addEventListener('click', (e) => {
      if (e.target.matches('.quantity__button') || 
          e.target.closest('.quantity__button') ||
          e.target.matches('[name="plus"], [name="minus"]') ||
          e.target.closest('[name="plus"], [name="minus"]')) {
        setTimeout(() => {
          this.updatePricing();
        }, 50);
      }
    });

    // Listen for Recharge subscription option selection changes
    document.addEventListener('change', (e) => {
      const target = e.target;
      if (target && target.type === 'radio' && target.closest('.shopify-block.recharge-subscription-widget')) {
        setTimeout(() => {
          this.updatePricing();
        }, 50);
      }
    });

    // If Recharge widget uses Shadow DOM, observe it for changes to radio inputs
    this.initSubscriptionMutationObserver();
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  updatePricing() {
    if (!this.totalPriceContainer || !this.totalPriceContainer.isConnected || !this.totalPriceElements) {
      this.createTotalPriceDisplay();
    }

    let elements = this.totalPriceElements;
    const hasDisplay = !!(
      this.totalPriceContainer && this.totalPriceContainer.isConnected &&
      elements && elements.current && elements.originalWrapper && elements.savingsWrapper
    );

    const quantity = this.getCurrentQuantity();
    const variantInfo = this.getVariantPricingInfo();

    if (!variantInfo || quantity < 1) return;

    const isSubSelected = this.isSubscriptionSelected();
    const effectiveUnitPrice = isSubSelected ? variantInfo.unitPrice * 0.95 : variantInfo.unitPrice;

    let totalPrice = effectiveUnitPrice * quantity;
    const formatPrice = (price) => `$${price.toFixed(2)}`;

    let originalTotal = 0;
    let savings = 0;

    if (isSubSelected) {
      originalTotal = variantInfo.unitPrice * quantity;
      totalPrice = originalTotal * 0.95;
      savings = originalTotal - totalPrice;
    } else if (variantInfo.firstVariantPrice > 0) {
      originalTotal = variantInfo.firstVariantPrice * variantInfo.packQuantity * quantity;
      savings = originalTotal - totalPrice;
    }

    if (savings < 0) savings = 0;

    // Update the on-page total display only when it exists. The price event below MUST
    // always fire so the Recharge widget multiplies by quantity even on product pages
    // that have no standalone total element.
    if (hasDisplay) {
      const isFirstVariant = this.isFirstVariantSelected();
      const showSavings = (isSubSelected && originalTotal > 0 && savings > 0.005)
        || (!isSubSelected && !isFirstVariant && originalTotal > 0 && savings > 0.005);

      const currentEl = elements.current;
      const originalWrapper = elements.originalWrapper;
      const originalValueEl = elements.originalValue || elements.originalWrapper;
      const savingsWrapper = elements.savingsWrapper;
      const savingsValueEl = elements.savingsValue || elements.savingsWrapper;

      if (currentEl) currentEl.textContent = formatPrice(totalPrice);

      if (showSavings && originalWrapper && originalValueEl && savingsWrapper && savingsValueEl) {
        originalWrapper.style.display = 'flex';
        originalValueEl.textContent = formatPrice(originalTotal);
        savingsWrapper.style.display = 'flex';
        savingsValueEl.textContent = formatPrice(savings);
      } else {
        if (originalWrapper) originalWrapper.style.display = 'none';
        if (savingsWrapper) savingsWrapper.style.display = 'none';
      }
    }

    document.dispatchEvent(new CustomEvent('nonsprout:pricing:updated', {
      detail: {
        quantity: quantity,
        unitPrice: variantInfo.unitPrice,
        totalPrice: totalPrice,
        packQuantity: variantInfo.packQuantity,
        firstVariantPrice: variantInfo.firstVariantPrice,
        originalTotal,
        savings,
        subscriptionSelected: isSubSelected
      }
    }));
  }

  getCurrentQuantity() {
    const scope =
      (this.totalPriceContainer && this.totalPriceContainer.closest('.product-form__quantity')) ||
      this.getProductContext();

    const quantityInput =
      scope.querySelector('input[name="quantity"].quantity__input') ||
      scope.querySelector('.quantity__input[name="quantity"]') ||
      scope.querySelector('.quantity__input') ||
      scope.querySelector('input[name="quantity"]');

    const parsed = quantityInput ? parseInt(quantityInput.value, 10) : NaN;
    return !isNaN(parsed) && parsed > 0 ? parsed : 1;
  }

  getVariantPricingInfo() {
    // Get pricing info from current variant selection
    return this.getAlternativeVariantPricing();
  }

  getAlternativeVariantPricing() {
    const scope = this.getProductContext();

    const pricingInfo = {
      unitPrice: 0,
      packQuantity: 1,
      firstVariantPrice: 0
    };
    
    // Pull numbers straight from the selected variant radio input (data attributes added in Liquid)
    const checkedRadio = scope.querySelector('.variant-price-button input[type="radio"]:checked');
    if (checkedRadio) {
      const packPrice   = parseFloat(checkedRadio.dataset.packPrice);
      const packQtyAttr = parseInt(checkedRadio.dataset.packQty);
      const singlePrice = parseFloat(checkedRadio.dataset.singlePrice);

      if (!isNaN(packPrice))  pricingInfo.unitPrice = packPrice;
      if (!isNaN(packQtyAttr)) pricingInfo.packQuantity = packQtyAttr;
      if (!isNaN(singlePrice)) pricingInfo.firstVariantPrice = singlePrice;

      return pricingInfo;
    }

    // Fallback to previous variant-ID based logic (unchanged) -- keep minimal
    let selectedVariantId = null;
    const variantSelectors = [
      'select[name="id"]',
      'input[name="id"]:checked',
      'input[name="id"][type="hidden"]'
    ];
    for (const selector of variantSelectors) {
      const element = scope.querySelector(selector);
      if (element && element.value) {
        selectedVariantId = element.value;
        break;
      }
    }

    if (selectedVariantId && window.product && window.product.variants) {
      const variant = window.product.variants.find(v => v.id.toString() === selectedVariantId.toString());
      if (variant) {
        pricingInfo.unitPrice = variant.price / 100;
        const firstVariantPrice = this.getFirstVariantPrice();
        pricingInfo.firstVariantPrice = firstVariantPrice;

        if (!pricingInfo.packQuantity || pricingInfo.packQuantity < 1) {
          pricingInfo.packQuantity = this.inferPackQuantity(variant, firstVariantPrice);
        }
      }
    }
    
    // Fallback to price elements on the page
    const priceSelectors = [
      '.price__regular .price-item',
      '.price__sale .price-item',
      '.price-item--regular',
      '.price-item--sale',
      '.product-price',
      '[data-product-price]'
    ];
    
    for (const selector of priceSelectors) {
      const element = scope.querySelector(selector);
      if (element) {
        pricingInfo.unitPrice = this.extractPriceFromText(element.textContent);
        pricingInfo.firstVariantPrice = this.getFirstVariantPrice();
        if (!pricingInfo.packQuantity || pricingInfo.packQuantity < 1) {
          pricingInfo.packQuantity = 1;
        }
        return pricingInfo;
      }
    }

    if (!pricingInfo.unitPrice || pricingInfo.unitPrice <= 0) {
      return null;
    }

    if (!pricingInfo.packQuantity || pricingInfo.packQuantity < 1) {
      pricingInfo.packQuantity = 1;
    }

    return pricingInfo;
  }

  inferPackQuantity(variant, basePrice) {
    if (!variant) return 1;

    const parseQuantity = (text) => {
      if (!text) return null;
      const normalized = text.toString().toLowerCase();

      const keywordMatch = normalized.match(/(\d+)\s*(?:pack|pk|case|box|ct|count|bundle|unit|units?)/i);
      if (keywordMatch) {
        const value = parseInt(keywordMatch[1], 10);
        if (!isNaN(value) && value > 0) {
          return value;
        }
      }

      const multiplierMatch = normalized.match(/(\d+)\s*[x×]\s*(\d+)/);
      if (multiplierMatch) {
        const value = parseInt(multiplierMatch[1], 10);
        if (!isNaN(value) && value > 0) {
          return value;
        }
      }

      return null;
    };

    if (Array.isArray(variant.options)) {
      for (const opt of variant.options) {
        const qty = parseQuantity(opt);
        if (qty) return qty;
      }
    }

    const titleQuantity = parseQuantity(variant.title);
    if (titleQuantity) return titleQuantity;

    const base = typeof basePrice === 'number' ? basePrice : 0;
    if (base > 0 && variant.price) {
      const ratio = (variant.price / 100) / base;
      const rounded = Math.round(ratio);
      if (rounded >= 1 && Math.abs(ratio - rounded) < 0.15) {
        return rounded;
      }
    }

    return 1;
  }

  getFirstVariantPrice() {
    // Prefer a variant explicitly representing ONE unit ("Single" / "1 Pack")
    if (window.product && Array.isArray(window.product.variants) && window.product.variants.length > 0) {
      const explicitSingle = window.product.variants.find(v => {
        const t = (v.title || '').toLowerCase();
        return t === 'single' || t.startsWith('1 pack') || t.startsWith('single pack') || t.includes('1 unit');
      });

      if (explicitSingle && typeof explicitSingle.price === 'number') {
        return explicitSingle.price / 100;
      }

      // Fallback: choose the variant whose parsed pack qty equals 1
      const variantWithQty1 = window.product.variants.find(v => {
        const t = (v.title || '').toLowerCase();
        const m = t.match(/(\d+)\s*pack/);
        return m && parseInt(m[1]) === 1;
      });

      if (variantWithQty1 && typeof variantWithQty1.price === 'number') {
        return variantWithQty1.price / 100;
      }

      // Last fallback: use FIRST variant price
      return window.product.variants[0].price / 100;
    }

    // Fallback: try to get price from Liquid-rendered elements
    const priceSelectors = [
      '.price__regular .price-item',
      '.price__sale .price-item',
      '.price-item--regular',
      '.price-item--sale',
      '.product-price',
      '[data-product-price]'
    ];

    for (const selector of priceSelectors) {
      const element = scope.querySelector(selector);
      if (element) {
        const p = this.extractPriceFromText(element.textContent);
        if (p > 0) return p;
      }
    }
    return 0;
  }

  extractPriceFromText(text) {
    if (!text) return 0;
    const match = text.replace(/[^\d.,]/g, '').replace(/,/g, '.');
    const price = parseFloat(match);
    return isNaN(price) ? 0 : price;
  }

  isFirstVariantSelected() {
    // Prefer explicit data-base-variant flag set in Liquid or a pack quantity of exactly 1
    const scope = this.getProductContext();

    const checkedRadio = scope.querySelector('.variant-price-button input[type="radio"]:checked');
    if (checkedRadio) {
      // 1. Explicit base flag injected by Liquid
      if (checkedRadio.dataset.baseVariant === 'true') return true;

      // 2. Fallback: pack size (data-pack-qty) equals 1
      if (checkedRadio.dataset.packQty === '1' || checkedRadio.dataset.packQty === 1) return true;
    }

    // Fallback to comparing against first variant in product JSON
    const variantSelectors = [
      'select[name="id"]',
      'input[name="id"]:checked',
      'input[name="id"][type="hidden"]'
    ];

    let selectedId = null;
    for (const selector of variantSelectors) {
      const el = scope.querySelector(selector);
      if (el && el.value) {
        selectedId = el.value.toString();
        break;
      }
    }

    if (!(window.product && Array.isArray(window.product.variants) && window.product.variants.length > 0)) {
      return false;
    }

    const firstVariantId = window.product.variants[0].id.toString();

    if (selectedId) {
      const variant = window.product.variants.find((v) => v.id.toString() === selectedId);
      if (variant) {
        const basePrice = this.getFirstVariantPrice();
        const packQty = this.inferPackQuantity(variant, basePrice);
        if (packQty === 1) {
          return true;
        }
      }
    }

    return selectedId === firstVariantId;
  }

  // Detect if the Recharge "subscribe & save" option is chosen
  isSubscriptionSelected() {
    const widget = document.querySelector('.shopify-block.recharge-subscription-widget');
    if (!widget) return false;

    // Helper to search through nested shadow roots
    const deepQuery = (root, selector) => {
      if (!root) return null;
      // 1. Query within the current root/light DOM
      const el = root.querySelector(selector);
      if (el) return el;

      // 2. If this node hosts a shadow root, search inside it before descending further
      if (root.shadowRoot) {
        const foundInShadow = deepQuery(root.shadowRoot, selector);
        if (foundInShadow) return foundInShadow;
      }

      // 3. Recurse through child elements (and their respective shadowRoots)
      const children = Array.from(root.children || []);
      for (const child of children) {
        const found = deepQuery(child, selector);
        if (found) return found;
      }
      return null;
    };

    // Primary check using deep query for rc-selected subscription option
    const subEl = deepQuery(widget, '.rc-purchase-option__subscription[rc-selected]');
    if (subEl) return true;

    // Fallback: look for checked radio inputs recursively
    const findCheckedRadio = (root) => {
      if (!root) return null;
      const radio = root.querySelector('input[type="radio"]:checked');
      if (radio) return radio;
      const children = Array.from(root.children || []);
      for (const child of children) {
        if (child.shadowRoot) {
          const found = findCheckedRadio(child.shadowRoot);
          if (found) return found;
        }
      }
      return null;
    };

    const checked = findCheckedRadio(widget.shadowRoot || widget);
    if (!checked) return false;

    const textVal = (checked.value || '').toLowerCase();
    if (textVal.includes('subscribe') || textVal.includes('delivery')) return true;

    const lbl = checked.closest('label');
    if (lbl && lbl.textContent.toLowerCase().includes('subscribe')) return true;

    return false;
  }

  /**
   * Sets up a MutationObserver on the Recharge subscription widget (including Shadow DOM)
   * so that changes to radio-button selection trigger a pricing update.
   */
  initSubscriptionMutationObserver() {
    const widget = document.querySelector('.shopify-block.recharge-subscription-widget');
    if (!widget) return;

    // Helper to recursively attach a mutation observer to a root and its shadow roots
    const attachObserverDeep = (root) => {
      if (!root) return;

      // Avoid duplicate observers by marking the root
      if (root.__nspObserverAttached) return;
      root.__nspObserverAttached = true;

      const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes') {
            const isRadioCheckedChange = m.target.nodeName === 'INPUT' && m.target.type === 'radio' && m.attributeName === 'checked';
            const isRechargeSelectedChange = m.attributeName === 'rc-selected';
            if (isRadioCheckedChange || isRechargeSelectedChange) {
              this.updatePricing();
              return;
            }
          }
          if (m.type === 'childList') {
            // Watch any newly added nodes for attribute changes as well
            m.addedNodes.forEach((n) => {
              if (n.shadowRoot) attachObserverDeep(n.shadowRoot);
              if (n.children) Array.from(n.children).forEach((c) => c.shadowRoot && attachObserverDeep(c.shadowRoot));
            });
            this.updatePricing();
          }
        }
      });

      observer.observe(root, {
        attributes: true,
        subtree: true,
        childList: true,
        attributeFilter: ['checked', 'rc-selected']
      });
    };

    // Attach observer to the widget element, its shadow root, and any nested shadow roots
    attachObserverDeep(widget);
    if (widget.shadowRoot) attachObserverDeep(widget.shadowRoot);

    // Fallback: update on any click within the widget (captures interactions the observers might miss)
    widget.addEventListener(
      'click',
      () => {
        // slight delay so Recharge can apply its attribute updates first
        setTimeout(() => this.updatePricing(), 50);
      },
      true
    );
  }
}

// Initialize when DOM is ready
if (typeof window !== 'undefined') {
  // Initialize the non-sprout total pricing
  window.NonSproutTotalPricing = NonSproutTotalPricing;
  window.nonSproutTotalPricingInstance = new NonSproutTotalPricing();
} 
